import { Suspense, useEffect } from "react";
import { retryLazy } from "@claroty/utilities/asyncUtils";
import { isProduction } from "@claroty/utilities/infra/environmentUtils";
import { LOADER_DURATION_NAMES, onPageView } from "../../utils/tracking";
import { RouteLoadingProvider } from "../../utils/tracking/tracing/RouteLoading";
import PageWithLoader from "../common/LoadingContainer/PageWithLoader";
import Redirect from "../common/Redirect/Redirect";
import Route from "../common/Route/Route";
import { RedirectRouteDefType, RouteDef } from "./RouteSpecType";

// Temp util to call onPageView when the route page loads (we wrap the import because we need to wrap the component before it gets to lazy).
// TODO: move this to Page component and use that for all pages, then replace "lazyWithRetryAndPageAnalytics" with "retryLazy"
//  or calculate the real page name here instead of taking it from the HTML element (after render)
const withPageAnalytics = Component => props => {
    useEffect(() => {
        // timeout is needed to wait for data load + page render to update page title
        const timeout = setTimeout(() => onPageView(), 1000);
        return () => clearTimeout(timeout);
    }, []);
    return <Component {...props} />;
};

export const lazyWithRetryAndPageAnalytics = importFunc =>
    retryLazy(async () => {
        try {
            const { default: Component } = await importFunc();
            if (!Component) throw "no_default";
            return { default: withPageAnalytics(Component) };
        } catch (ex) {
            if (!isProduction) console.error(ex); // eslint-disable-line
            throw ex;
        }
    }, importFunc);

/**
 * this is a render function and not a component because Routes have to be direct children of Switch
 * Note that Switch looks at the children's props to determine if they are a match, so we don't need
 * to use the "path" or "exact" props in our Redirect component
 **/
export const renderRedirects = (redirects: RedirectRouteDefType[]) =>
    redirects.map(({ from, to, preserveSearch = false, preserveParams }) => (
        <Redirect
            key={from}
            shouldCopyState
            path={from}
            exact
            preserveParams={preserveParams}
            to={preserveSearch ? { pathname: to, search: window.location.search } : to}
        />
    ));

/**
 * this is a render function and not a component because Routes have to be direct children of Switch
 **/
export const renderRoutes = (
    routes: Array<RouteDef>,
    { appProps = {}, globalFilters = undefined, showBreadcrumbsInSuspense = false } = {},
) =>
    routes
        .flatMap(routeDef =>
            routeDef ? [routeDef, ...((routeDef.childRoutes as Array<RouteDef>)?.filter(Boolean) ?? [])] : [],
        )
        .filter(routeDef => routeDef.component)
        .map(
            (
                {
                    path,
                    key,
                    componentKey,
                    props: routeProps,
                    component: Component,
                    hasChildRouter,
                    suppressLocationKeyRemount,
                    routeTrackingName,
                },
                index,
            ) => (
                <Route
                    exact={!hasChildRouter}
                    path={path}
                    key={key || index}
                    globalFilters={globalFilters}
                    suppressLocationKeyRemount={suppressLocationKeyRemount}
                    render={props => (
                        <RouteLoadingProvider
                            routeTrackingName={routeTrackingName}
                            routePath={path}
                            shouldTrackLoading={!!routeTrackingName}>
                            <Suspense
                                fallback={
                                    <PageWithLoader
                                        trackingName={LOADER_DURATION_NAMES.LAZY_MODULE_LOADER}
                                        showBreadcrumbs={showBreadcrumbsInSuspense}
                                    />
                                }>
                                <Component
                                    key={componentKey && componentKey(props)}
                                    {...props}
                                    {...appProps}
                                    {...routeProps}
                                />
                            </Suspense>
                        </RouteLoadingProvider>
                    )}
                />
            ),
        );
